import { useCallback, useEffect, useState } from "react";
import { useCanvasOffsets } from "@circle-react-shared/BuilderUI/CanvasTools/useCanvasOffsets";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";

export interface NonIframePositions {
  elementRect: DOMRect;
  iframeRect: DOMRect;
}

export interface UseNonIframeCanvasToolsPositionsReturn {
  positions: NonIframePositions | null;
  selectedBlock: { id: string } | null;
  updatePosition: () => void;
}

export const useNonIframeCanvasToolsPositions =
  (): UseNonIframeCanvasToolsPositionsReturn => {
    const { selectedBlock } = useBuilderStore(state => {
      const value = state.getSelectedBlock();
      return {
        selectedBlockPath: state.selectedBlockPath,
        selectedBlock: value?.block,
      };
    });
    const { offsetX, offsetY } = useCanvasOffsets();
    const [positions, setElementRect] = useState<NonIframePositions | null>(
      null,
    );

    const getElementRect = useCallback(() => {
      if (!selectedBlock?.id) {
        return null;
      }

      const element = document.getElementById(selectedBlock.id);
      if (!element) {
        return null;
      }

      const elementRect = element.getBoundingClientRect();
      const wrapperElement = document.documentElement;
      const wrapperRect = wrapperElement.getBoundingClientRect();

      const adjustedElementRect = {
        ...elementRect,
        top: elementRect.top + offsetY,
        left: elementRect.left + offsetX,
        right: elementRect.right + offsetX,
        bottom: elementRect.bottom + offsetY,
        width: elementRect.width,
        height: elementRect.height,
      } as DOMRect;

      return {
        elementRect: adjustedElementRect,
        iframeRect: wrapperRect,
        absoluteTop: wrapperRect.top + elementRect.top + offsetY,
        absoluteLeft: wrapperRect.left + elementRect.left + offsetX,
        absoluteRight: wrapperRect.left + elementRect.right + offsetX,
        absoluteBottom: wrapperRect.top + elementRect.bottom + offsetY,
      };
    }, [selectedBlock, offsetX, offsetY]);

    const updatePosition = useCallback(() => {
      const rect = getElementRect();
      setElementRect(rect);
    }, [getElementRect]);

    useEffect(() => {
      updatePosition();
    }, [updatePosition]);

    return {
      positions,
      selectedBlock: selectedBlock || null,
      updatePosition,
    };
  };
